/* eslint-disable import/no-cycle */
import axios, { AxiosRequestConfig, AxiosError } from 'axios'
import { notification, message as antMessage } from 'antd'
import { get, isEmpty, forEach, lowerCase, has } from 'lodash'
import i18next from 'i18next'
import qs from 'qs'

// types
import { IErrorMessage } from '../types/interfaces'

// reducers
import { logOutUser } from '../reducers/authorization/authActions'
import configureStore from './configureStore'
import rootReducer from '../reducers'

// utils
import { translateMessageType } from './helper'
import { NOTIFICATION_TYPE, MSG_TYPE } from './enums'
import { getAccessToken, isLoggedIn } from './auth'
import Navigator from './navigation'

const { store } = configureStore(rootReducer)

export const showNotifications = (messages: IErrorMessage[], typeNotification: NOTIFICATION_TYPE) => {
	if (!isEmpty(messages)) {
		if (typeNotification === NOTIFICATION_TYPE.NOTIFICATION) {
			forEach(messages, (message) => {
				let notif: any
				switch (lowerCase(message.type)) {
					case 'info':
						notif = notification.info
						break
					case 'warning':
						notif = notification.warning
						break
					case 'success':
						notif = notification.success
						break
					case 'error':
						notif = notification.error
						break
					default:
						notif = notification.info
						break
				}
				notif({
					message: translateMessageType(message.type),
					description: message.message
				})
			})
		} else if (typeNotification === NOTIFICATION_TYPE.MODAL) {
			// TODO: doriesit modal notification
		}
	}
}

export const showErrorNotifications = (error: AxiosError | Error, typeNotification = NOTIFICATION_TYPE.NOTIFICATION) => {
	let messages: IErrorMessage[] = get(error, 'response.data.messages', [])

	if (get(error, 'response.status') === 401) {
		if (isLoggedIn()) {
			messages = [
				{
					type: MSG_TYPE.INFO,
					message: i18next.t('loc:Boli ste automaticky odhlásený')
				}
			]
		}
		showNotifications(messages, typeNotification)
		logOutUser()(store.dispatch, store.getState, undefined)
		Navigator.navigate(i18next.t('paths:prihlasenie') as string)
	} else if (get(error, 'response.status') === 504 || get(error, 'response') === undefined || get(error, 'message') === 'Network Error') {
		messages = [
			{
				type: MSG_TYPE.ERROR,
				message: i18next.t('loc:Chyba pripojenia k serveru')
			}
		]
		showNotifications(messages, typeNotification)
	} else {
		// if BE do not send message set general error message
		messages = isEmpty(messages) ? [{ type: MSG_TYPE.ERROR, message: i18next.t('loc:Ups niečo sa pokazilo') }] : messages
		showNotifications(messages, typeNotification)
	}
}

interface IHeader {
	[key: string]: string | boolean | undefined
}

interface ICustomConfig extends AxiosRequestConfig {
	messages?: IErrorMessage[]
}

const buildHeaders = () => {
	const headers: IHeader = {
		'Content-Type': 'application/json',
		Accept: 'application/json',
		'Access-Control-Allow-Credentials': true,
		'Cache-Control': 'no-cache, no-store',
		Pragma: 'no-cache',
		'X-Version': process.env.REACT_APP_VERSION,
		// hardcoded language due to backend translations
		'Accept-Language': 'en-US;q=0.7,en;q=0.6'
	}
	if (isLoggedIn()) {
		headers.Authorization = `Bearer ${getAccessToken()}`
	}

	return headers
}

/**
 * @param url url endpoint
 * @param params Object object
 * @param customConfig overwrite defaultConfig with custom one
 * @param typeNotification Enum notification type
 * @return Promise response
 *
 */
export const getReq = async (url: string, params?: any, customConfig: ICustomConfig = {}, typeNotification?: NOTIFICATION_TYPE, showLoading = false) => {
	let hide
	if (showLoading) {
		hide = antMessage.loading('Načitavajú sa dáta...', 0)
	}
	const config: AxiosRequestConfig = {
		paramsSerializer: qs.stringify as any,
		...customConfig,
		headers: {
			...buildHeaders(),
			...get(customConfig, 'headers', {})
		}
	}

	if (params) {
		config.params = params
	}

	try {
		const res = await axios.get(url, config)
		if (typeNotification) {
			if (customConfig && customConfig.messages) {
				showNotifications(customConfig.messages, typeNotification)
			} else if (has(res, 'data.messages')) {
				showNotifications(res?.data?.messages, typeNotification)
			}
		}
		if (hide) {
			hide()
		}

		return res
	} catch (e: any) {
		showErrorNotifications(e, typeNotification)
		if (hide) {
			hide()
		}
		return Promise.reject(e)
	}
}

/**
 * @param url url endpoint
 * @param params Object params object
 * @param data Object data object
 * @param customConfig overwrite defaultConfig with custom one
 * @param typeNotification Enum notification type
 * @param showLoading Boolean show loading
 * @return Promise response
 * Performs post request to url and returns callback with result
 */
export const postReq = async (
	url: string,
	params: any,
	data: any = {},
	customConfig: ICustomConfig = {},
	typeNotification?: NOTIFICATION_TYPE,
	showLoading = false
) => {
	let hide
	if (showLoading) {
		hide = antMessage.loading('Operácia sa vykonáva...', 0)
	}
	const config = {
		...customConfig,
		headers: {
			...buildHeaders(),
			...get(customConfig, 'headers', {})
		}
	}

	if (params) {
		config.params = params
	}

	try {
		const res = await axios.post(url, data, config)
		if (typeNotification) {
			if (customConfig && customConfig.messages) {
				showNotifications(customConfig.messages, typeNotification)
			} else if (has(res, 'data.messages')) {
				showNotifications(res?.data?.messages, typeNotification)
			}
		}

		if (hide) {
			hide()
		}
		return res
	} catch (e: any) {
		if (hide) {
			hide()
		}
		showErrorNotifications(e, typeNotification)

		return Promise.reject(e)
	}
}

/**
 * @param url url endpoint
 * @param params Object params object
 * @param data Object data object
 * @param customConfig overwrite defaultConfig with custom one
 * @param typeNotification Enum notification type
 *
 * Performs put request to url and returns callback with result
 */
export const patchReq = async (
	url: string,
	params: any,
	data: any = {},
	customConfig: ICustomConfig = {},
	showLoading = false,
	typeNotification?: NOTIFICATION_TYPE
) => {
	let hide
	if (showLoading) {
		hide = antMessage.loading('Operácia sa vykonáva...', 0)
	}
	const config = {
		...customConfig,
		headers: {
			...buildHeaders(),
			...get(customConfig, 'headers', {})
		}
	}

	if (params) {
		config.params = params
	}
	try {
		const res = await axios.patch(url, data, config)
		if (typeNotification && customConfig && customConfig.messages) {
			showNotifications(customConfig.messages, typeNotification)
		} else if (typeNotification && has(res, 'data.messages')) {
			showNotifications(res?.data?.messages, typeNotification)
		}
		if (hide) {
			hide()
		}
		return res
	} catch (e: any) {
		if (hide) {
			hide()
		}
		showErrorNotifications(e, typeNotification)
		return Promise.reject(e)
	}
}

/**
 * @param url url endpoint
 * @param params Object params object
 * @param customConfig overwrite defaultConfig with custom one
 * @param typeNotification Enum notification type
 * @param showLoading Boolean show loading
 *
 * Performs delete request to url and returns with result
 */
export const deleteReq = async (url: string, params: any, customConfig: ICustomConfig = {}, typeNotification?: NOTIFICATION_TYPE, showLoading = false) => {
	let hide
	if (showLoading) {
		hide = antMessage.loading('Operácia sa vykonáva...', 0)
	}

	const config = {
		...customConfig,
		headers: {
			...buildHeaders(),
			...get(customConfig, 'headers', {})
		}
	}

	if (params) {
		config.params = params
	}

	try {
		const res = await axios.delete(url, config)

		if (typeNotification && customConfig && customConfig.messages) {
			showNotifications(customConfig.messages, typeNotification)
		} else if (typeNotification && has(res, 'data.messages')) {
			showNotifications(res?.data?.messages, typeNotification)
		}

		if (hide) {
			hide()
		}

		return res
	} catch (e: any) {
		if (hide) {
			hide()
		}

		showErrorNotifications(e, typeNotification)
		return Promise.reject(e)
	}
}
